import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { withProject, type Session } from '@obraplan/core/application/session';
import type { ProjectId } from '@obraplan/core/domain/shared/ids';
import type { AppError } from '@obraplan/core/application/errors/app-error';
import { ERROR_CODE } from '@obraplan/core/application/errors/error-codes';
import type { Container } from '@/app/container';
import {
  ContainerContext,
  SessionContext,
  type SessionState,
  type SessionStatus,
} from './session-context';

/**
 * Sessão e obra ativas da aplicação.
 *
 * Abre o banco do aparelho na abertura e restaura a sessão pelo token guardado e a última
 * obra usada. Se o token não valer mais — vencido, revogado, perfil desativado —, a tela
 * volta a pedir a entrada em vez de falhar (RN-135). Se a obra salva não estiver mais
 * acessível ao perfil, a seleção de obra é limpa, também sem falhar.
 *
 * Sem sinal, a sessão não cai (RN-598): quem diz que o token não vale é o servidor, e sem
 * resposta dele não há o que dizer. A sessão vem da leitura guardada no aparelho; sem ela, a
 * tela diz que precisa de conexão e o token continua guardado.
 */

/** O que a tela diz quando há token, não há sinal e o aparelho não guardou a sessão. */
export const MENSAGEM_SEM_SESSAO_GUARDADA =
  'Sem conexão, e este aparelho ainda não guardou a sua sessão. Conecte-se à internet uma vez ' +
  'com o sistema aberto; depois disso, ele abre também sem sinal.';

/**
 * A falha que não é recusa: o servidor não respondeu (`API.SEM_CONEXAO`), respondeu algo que não
 * era a API (portal do Wi-Fi, proxy) ou caiu no meio (500). Nada disso diz que o token morreu.
 */
function semRespostaDoServidor(erro: AppError): boolean {
  return erro.kind === 'unexpected' || erro.code === ERROR_CODE.NO_CONNECTION;
}
export function AppProviders({
  container,
  children,
}: {
  container: Container;
  children: ReactNode;
}) {
  const [status, setStatus] = useState<SessionStatus>('initializing');
  const [baseSession, setBaseSession] = useState<Session | undefined>(undefined);
  const [activeProjectId, setActiveProjectId] = useState<ProjectId | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const initialized = useRef(false);

  /** Restaura a obra salva, descartando-a se o perfil não a alcança mais (RN-132). */
  const restoreProject = useCallback(
    async (session: Session) => {
      const storedProjectId = await container.getActiveProjectId.execute();
      if (storedProjectId === undefined) {
        setActiveProjectId(undefined);
        return;
      }
      const applied = await container.setActiveProject.execute(storedProjectId, session);
      if (applied.ok) {
        setActiveProjectId(applied.value);
      } else {
        await container.setActiveProject.execute(undefined, session);
        setActiveProjectId(undefined);
      }
    },
    [container],
  );

  const initialize = useCallback(async () => {
    try {
      await container.bootstrap();

      // A sessão é restaurada pelo **token** guardado no aparelho, e não pelo perfil
      // escolhido. Quem diz se ele ainda vale é o portão: token vencido, revogado ou de
      // perfil desativado devolve recusa, e a tela volta a pedir o e-mail.
      const token = await container.getSessionToken.execute();
      if (token === undefined) {
        setStatus('no-session');
        return;
      }

      const resolvida = await container.sessions.resolve(token);
      if (!resolvida.ok) {
        if (semRespostaDoServidor(resolvida.error)) {
          setErrorMessage(
            resolvida.error.code === ERROR_CODE.NO_CONNECTION
              ? MENSAGEM_SEM_SESSAO_GUARDADA
              : resolvida.error.message,
          );
          setStatus('offline');
          return;
        }
        await container.clearSessionToken.execute();
        setStatus('no-session');
        return;
      }

      setBaseSession(resolvida.value);
      setStatus('ready');
      await restoreProject(resolvida.value);
    } catch (cause) {
      setStatus('error');
      setErrorMessage(
        cause instanceof Error && cause.name === 'QuotaExceededError'
          ? 'O navegador bloqueou o armazenamento local.'
          : 'Não foi possível abrir o banco de dados local.',
      );
    }
  }, [container, restoreProject]);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    void initialize();
  }, [initialize]);

  /**
   * Guardar o segredo e abrir a sessão — o que vem depois da entrada.
   *
   * O segredo fica no aparelho — é o que evita pedir a senha toda manhã no tablet do
   * canteiro. Quem diz se ele vale é o portão, ao resolvê-lo.
   */
  const abrirSessao = useCallback(
    async (sessionSecret: string): Promise<string | undefined> => {
      await container.setSessionToken.execute(sessionSecret);

      const resolvida = await container.sessions.resolve(sessionSecret);
      if (!resolvida.ok) return resolvida.error.message;

      setBaseSession(resolvida.value);
      setErrorMessage(undefined);
      setStatus('ready');
      await restoreProject(resolvida.value);
      return undefined;
    },
    [container, restoreProject],
  );

  /**
   * Entra com e-mail e senha.
   *
   * Devolve a mensagem de falha em vez de lançar: credencial errada é caminho esperado, e
   * a tela precisa dizê-la em português — sempre a mesma, para as quatro falhas.
   */
  const signIn = useCallback(
    async (email: string, password: string): Promise<string | undefined> => {
      const entrada = await container.signInWithPassword.execute({ email, password });
      if (!entrada.ok) return entrada.error.message;
      return abrirSessao(entrada.value.sessionSecret);
    },
    [container, abrirSessao],
  );

  const selectProject = useCallback(
    async (projectId: ProjectId | undefined) => {
      if (baseSession === undefined) return;
      const result = await container.setActiveProject.execute(projectId, baseSession);
      if (!result.ok) {
        setErrorMessage(result.error.message);
        return;
      }
      setErrorMessage(undefined);
      setActiveProjectId(result.value);
    },
    [container, baseSession],
  );

  /**
   * Sair.
   *
   * Revoga a sessão **antes** de esquecer o token: na ordem inversa, o token sumiria do
   * aparelho e a sessão continuaria válida no banco, alcançável por quem tivesse uma
   * cópia dele. Revogar é o que encerra; esquecer é só limpeza.
   */
  const clearSession = useCallback(async () => {
    if (baseSession !== undefined) await container.signOut.execute({}, baseSession);
    await container.clearSessionToken.execute();
    setBaseSession(undefined);
    setActiveProjectId(undefined);
    setStatus('no-session');
  }, [container, baseSession]);

  // Recarrega pelo portão, com o token do aparelho — como o `initialize`. O `loadSession` lia o
  // perfil no banco do navegador, onde o usuário do servidor não existe: salvar "Meu perfil"
  // mandava a pessoa para a tela de entrada até ela recarregar a página. Se o portão não
  // responder, fica a sessão que estava: a próxima chamada à API diz se o token ainda vale.
  const reload = useCallback(async () => {
    if (baseSession === undefined) return;
    const token = await container.getSessionToken.execute();
    if (token === undefined) return;
    const resolvida = await container.sessions.resolve(token);
    if (resolvida.ok) setBaseSession(resolvida.value);
  }, [container, baseSession]);

  // A sessão entregue à interface já carrega a obra ativa: assim `usePermission` e os
  // casos de uso recebem o escopo correto sem que cada tela precise repassá-lo.
  const session = useMemo(
    () => (baseSession === undefined ? undefined : withProject(baseSession, activeProjectId)),
    [baseSession, activeProjectId],
  );

  const value = useMemo<SessionState>(
    () => ({
      status,
      session,
      errorMessage,
      activeProjectId,
      selectProject,
      signIn,
      clearSession,
      reload,
    }),
    [status, session, errorMessage, activeProjectId, selectProject, signIn, clearSession, reload],
  );

  return (
    <ContainerContext.Provider value={container}>
      <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
    </ContainerContext.Provider>
  );
}
